import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuLock, LuShieldCheck, LuUserRound } from "react-icons/lu";

import UsersToolbar from "./userPageComponents/UsersToolbar";
import UsersActionBar from "./userPageComponents/UsersActionBar";
import UserFormModal from "./userPageComponents/UserFormModal";
import Avatar from "../../common/Avatar";
import Badge from "../../common/Badge";
import ConfirmDialog from "../../common/ConfirmDialog";
import Toaster, { notify } from "../../common/Toast";
import TruncatedText from "../../common/TruncatedText";
import { formatPhone } from "../../utils/phone";
import {
  fetchUsers,
  addUser,
  editUser,
  deleteUsers,
  unlockUser,
  sendResetPasswordEmail,
} from "../../redux/action/userAction/UserAction";
import Loader from "../../common/Loader";

export default function Users() {
  const dispatch = useDispatch();
  const {
    list: data,
    listLoading,
    addLoading,
    editLoading,
    deleteLoading,
    unlockLoading,
  } = useSelector((state) => state.user);

  const [searchValue, setSearchValue] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [selectedRow, setSelectedRow] = useState(new Map());
  const [reloadKey, setReloadKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);

  // .................... Fetch (debounced search) ...........................

  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(fetchUsers(searchValue)).then((res) => {
        if (!res.success) {
          notify.error("Failed to fetch users", res.error);
        }
      });
    }, 350);
    return () => clearTimeout(handle);
  }, [dispatch, searchValue, reloadKey]);

  const refetch = () => setReloadKey((k) => k + 1);

  // .................... Client-side role/status filter ...........................

  const filteredData = data.filter((item) => {
    const matchesRole =
      role === "all" || (role === "admin" ? item.admin : !item.admin);
    const itemStatus = item.disabled
      ? "disabled"
      : item.lockedUntil
        ? "locked"
        : "active";
    const matchesStatus = status === "all" || status === itemStatus;
    return matchesRole && matchesStatus;
  });

  // .................... Selection ...........................

  const selectedCount = selectedRow.size;
  const selectedEmails = Array.from(selectedRow.keys());
  const singleSelected =
    selectedCount === 1
      ? { email: selectedEmails[0], ...selectedRow.get(selectedEmails[0]) }
      : null;

  const handleRowClick = (item) => {
    const updated = new Map(selectedRow);
    if (updated.has(item.email)) {
      updated.delete(item.email);
    } else {
      const { email, ...rest } = item;
      updated.set(email, rest);
    }
    setSelectedRow(updated);
  };

  const handleSelectAll = () => {
    if (selectedRow.size === 0) {
      setSelectedRow(
        new Map(filteredData.map(({ email, ...rest }) => [email, rest])),
      );
    } else {
      setSelectedRow(new Map());
    }
  };

  // .................... Add / Edit ...........................

  const openAddModal = () => {
    setFormMode("add");
    setFormOpen(true);
  };

  const openEditModal = () => {
    if (!singleSelected) return;
    setFormMode("edit");
    setFormOpen(true);
  };

  const handleFormSubmit = async (form) => {
    const action =
      formMode === "edit"
        ? editUser({ ...form, email: singleSelected.email })
        : addUser(form);
    const res = await dispatch(action);
    if (res.success) {
      notify.success(
        formMode === "edit" ? "Successful Update" : "Successful Addition",
        formMode === "edit"
          ? "User has been successfully updated."
          : "User has been successfully added.",
      );
      setFormOpen(false);
      setSelectedRow(new Map());
      refetch();
    } else {
      notify.error(
        formMode === "edit" ? "Failed to update user" : "Failed to add user",
        res.error,
      );
    }
  };

  // .................... Delete ...........................

  const handleDelete = async () => {
    const res = await dispatch(deleteUsers(selectedEmails));
    if (res.success) {
      notify.success(
        "Successful Deletion",
        "User(s) have been successfully deleted.",
      );
      setSelectedRow(new Map());
      setDeleteConfirmOpen(false);
      refetch();
    } else {
      notify.error("Failed to delete user(s)", res.error);
    }
  };

  // .................... Unlock ...........................

  const handleUnlock = async () => {
    if (!singleSelected) return;
    const res = await dispatch(unlockUser(singleSelected.email));
    if (res.success) {
      notify.success("User Unlocked", "User has been successfully unlocked.");
      setSelectedRow(new Map());
      setUnlockConfirmOpen(false);
      refetch();
    } else {
      notify.error("Failed to unlock user", res.error);
    }
  };

  // .................... Reset password email ...........................

  const handleResetPassword = async () => {
    if (!singleSelected) return;
    const res = await dispatch(
      sendResetPasswordEmail(singleSelected.email, singleSelected.name),
    );
    if (res.success) {
      notify.success(
        "Password Reset Email Sent",
        "Set password email has been sent successfully.",
      );
      setSelectedRow(new Map());
    } else {
      notify.error("Failed to send password reset email", res.error);
    }
  };

  return (
    <>
      {/* FIX: was `min-h-screen w-full pb-24` — min-h-screen forces >=100vh,
          then pb-24 added another 96px on top of that unconditionally, so the
          page was always taller than the viewport (hence the phantom
          scrollbar) regardless of how little data was in the table. Now it
          sizes to its actual content, and only reserves bottom space for the
          floating action bar while it's actually visible. */}
      <div className={`w-full ${selectedCount > 0 ? "pb-24" : ""}`}>
        <div className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-4 lg:px-6">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <UsersToolbar
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              role={role}
              status={status}
              onRoleChange={setRole}
              onStatusChange={setStatus}
              onAddClick={openAddModal}
            />

            {/* ---------------- Desktop table (md and up) ---------------- */}
            <div className="hidden md:block">
              <div className="max-h-[70vh] overflow-auto">
                <table className="w-full table-fixed text-left text-[13px]">
                  <colgroup>
                    <col className="w-10" />
                    <col className="w-[18%]" />
                    <col className="w-[20%]" />
                    <col className="hidden lg:table-column lg:w-[12%]" />
                    <col className="hidden xl:table-column xl:w-[18%]" />
                    <col className="hidden lg:table-column lg:w-[14%]" />
                    <col className="w-[100px]" />
                    <col className="w-[90px]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-slate-50/95 text-slate-500 backdrop-blur">
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={
                            selectedCount > 0 &&
                            selectedCount === filteredData.length
                          }
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                        />
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide"> Name </th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide"> Email</th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide lg:table-cell"> Contact</th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide xl:table-cell"> Address</th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide lg:table-cell"> Designation</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide"> Role</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide"> Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {listLoading && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-10 text-center text-slate-400"
                        >
                          <Loader fullScreen text="Loading Users ..." />
                        </td>
                      </tr>
                    )}
                    {!listLoading && filteredData.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-10 text-center text-slate-400"
                        >
                          No users found.
                        </td>
                      </tr>
                    )}
                    {!listLoading &&
                      filteredData.map((item) => {
                        const isSelected = selectedRow.has(item.email);
                        return (
                          <tr
                            key={item.email}
                            onClick={() => handleRowClick(item)}
                            className={`cursor-pointer border-l-2 transition-colors ${
                              isSelected
                                ? "border-l-brand-500 bg-brand-50/60"
                                : "border-l-transparent hover:bg-slate-50"
                            }`}
                          >
                            <td
                              className="px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleRowClick(item)}
                                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                              />
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">
                              <div className="flex min-w-0 items-center gap-2.5">
                                <Avatar name={item.name} size={30} />
                                <TruncatedText text={item.name} />
                                {item.lockedUntil && (
                                  <LuLock
                                    className="shrink-0 text-amber-500"
                                    size={13}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-500">
                              <TruncatedText text={item.email} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">
                              <TruncatedText text={formatPhone(item.contactNumber)} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 xl:table-cell">
                              <TruncatedText text={item.address} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">
                              <TruncatedText text={item.designation} />
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={item.admin ? "admin" : "member"}
                                icon={
                                  item.admin ? (
                                    <LuShieldCheck size={11} />
                                  ) : (
                                    <LuUserRound size={11} />
                                  )
                                }
                              >
                                {item.admin ? "Admin" : "Member"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={
                                  item.disabled
                                    ? "disabled"
                                    : item.lockedUntil
                                      ? "locked"
                                      : "active"
                                }
                              >
                                {item.disabled
                                  ? "Disabled"
                                  : item.lockedUntil
                                    ? "Locked"
                                    : "Active"}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ---------------- Mobile cards (below md) ---------------- */}
            <div className="divide-y divide-slate-100 md:hidden">
              {listLoading && (
                <div className="px-4 py-10 text-center text-[13px] text-slate-400">
                  <Loader fullScreen text="Loading Users ..." />
                </div>
              )}
              {!listLoading && filteredData.length === 0 && (
                <div className="px-4 py-10 text-center text-[13px] text-slate-400">
                  No users found.
                </div>
              )}
              {!listLoading &&
                filteredData.map((item) => {
                  const isSelected = selectedRow.has(item.email);
                  return (
                    <div
                      key={item.email}
                      onClick={() => handleRowClick(item)}
                      className={`flex items-start gap-3 border-l-2 px-4 py-3 transition-colors ${
                        isSelected
                          ? "border-l-brand-500 bg-brand-50/60"
                          : "border-l-transparent active:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRowClick(item)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                      />
                      <Avatar name={item.name} size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-slate-800">
                          <TruncatedText text={item.name} />
                          {item.lockedUntil && (
                            <LuLock className="shrink-0 text-amber-500" size={12} />
                          )}
                        </div>
                        <TruncatedText text={item.email} className="text-[12px] text-slate-500" />
                        {(item.contactNumber || item.designation) && (
                          <TruncatedText
                            text={[item.designation, formatPhone(item.contactNumber)]
                              .filter(Boolean)
                              .join(" · ")}
                            className="text-[12px] text-slate-400"
                          />
                        )}
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <Badge variant={item.admin ? "admin" : "member"}>
                            {item.admin ? "Admin" : "Member"}
                          </Badge>
                          <Badge
                            variant={
                              item.disabled
                                ? "disabled"
                                : item.lockedUntil
                                  ? "locked"
                                  : "active"
                            }
                          >
                            {item.disabled
                              ? "Disabled"
                              : item.lockedUntil
                                ? "Locked"
                                : "Active"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <UsersActionBar
          selectedCount={selectedCount}
          canEdit={selectedCount === 1}
          canUnlock={selectedCount === 1 && !!singleSelected?.lockedUntil}
          onEdit={openEditModal}
          onDelete={() => setDeleteConfirmOpen(true)}
          onUnlock={() => setUnlockConfirmOpen(true)}
          onResetPassword={handleResetPassword}
          onClear={() => setSelectedRow(new Map())}
        />
      </div>

      <UserFormModal
        open={formOpen}
        mode={formMode}
        initialValue={formMode === "edit" ? singleSelected : null}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        loading={formMode === "edit" ? editLoading : addLoading}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete user(s)?"
        description={`This will permanently delete ${selectedCount} user${
          selectedCount > 1 ? "s" : ""
        }. This action cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
        loading={deleteLoading}
      />

      <ConfirmDialog
        open={unlockConfirmOpen}
        onClose={() => setUnlockConfirmOpen(false)}
        onConfirm={handleUnlock}
        title="Unlock user?"
        description={
          singleSelected
            ? `${singleSelected.name} will be able to log in again.`
            : ""
        }
        confirmLabel="Unlock"
        tone="success"
        loading={unlockLoading}
      />

      <Toaster />
    </>
  );
}